import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Table, Button, Badge, Alert, Spinner, Modal, Form } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { shippingAPI } from '../utils/api';

const ShippingManagement = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [deliveryDate, setDeliveryDate] = useState('');
  const [note, setNote] = useState('');

  // Ref to prevent duplicate API calls
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true;
      loadShippingOrders();
    }
  }, []);

  const loadShippingOrders = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await shippingAPI.getAll();
      setOrders(res.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'เกิดข้อผิดพลาดในการโหลดข้อมูล');
    } finally {
      setLoading(false);
    }
  };

  const handleSetDeliveryDate = (order) => {
    setSelectedOrder(order);
    setDeliveryDate(order.delivery?.deliveryDate || '');
    setNote(order.delivery?.note || '');
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedOrder || !deliveryDate) return;

    try {
      setProcessing(true);
      await shippingAPI.createOrUpdate(selectedOrder.id, {
        deliveryDate,
        note
      });
      alert('กำหนดวันที่จัดส่งสำเร็จ');
      setShowModal(false);
      loadShippingOrders();
    } catch (err) {
      alert(err.response?.data?.message || 'เกิดข้อผิดพลาด');
    } finally {
      setProcessing(false);
    }
  };

  const handleDelete = async (orderId) => {
    if (!window.confirm('ต้องการลบวันที่จัดส่งหรือไม่?')) {
      return;
    }

    try {
      await shippingAPI.delete(orderId);
      alert('ลบวันที่จัดส่งสำเร็จ');
      loadShippingOrders();
    } catch (err) {
      alert(err.response?.data?.message || 'เกิดข้อผิดพลาดในการลบ');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('th-TH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('th-TH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Container fluid className="main-content">
      <Row className="mb-4">
        <Col>
          <h2>
            <i className="bi bi-truck me-2"></i>
            จัดการการจัดส่ง
          </h2>
          <p className="text-muted">กำหนดวันที่จัดส่งสำหรับคำสั่งซื้อที่อนุมัติแล้ว</p>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Card>
        <Card.Header className="bg-primary bg-opacity-10">
          <Row>
            <Col>
              <h5 className="mb-0">
                <i className="bi bi-list-check me-2"></i>
                คำสั่งซื้อที่อนุมัติแล้ว ({orders.length} รายการ)
              </h5>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3 text-muted">กำลังโหลดข้อมูล...</p>
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-inbox" style={{ fontSize: '3rem', color: '#999' }}></i>
              <p className="text-muted mt-3">ไม่มีคำสั่งซื้อ</p>
            </div>
          ) : (
            <Table responsive hover className="mb-0">
              <thead className="table-light">
                <tr>
                  <th>รหัสคำสั่งซื้อ</th>
                  <th>สาขา</th>
                  <th>ผู้รับ</th>
                  <th>จำนวนรายการ</th>
                  <th>มูลค่า</th>
                  <th>วันที่จัดส่ง</th>
                  <th>กำหนดโดย</th>
                  <th className="text-center">การจัดการ</th>
                </tr>
              </thead>
              <tbody>
                {orders.map(order => (
                  <tr key={order.id}>
                    <td><strong>#{order.id}</strong></td>
                    <td>{order.orderedBy?.fullName || '-'}</td>
                    <td>
                      {order.recipientName || '-'}
                      {order.recipientPhone && (
                        <><br /><small className="text-muted">{order.recipientPhone}</small></>
                      )}
                    </td>
                    <td className="text-center">
                      <Badge bg="info">{order.items?.length || 0} รายการ</Badge>
                    </td>
                    <td className="text-end">
                      <strong>{order.totalCost?.toLocaleString('th-TH', { minimumFractionDigits: 2 })} ฿</strong>
                    </td>
                    <td>
                      {order.delivery?.deliveryDate ? (
                        <>
                          <Badge bg="success" className="me-2">
                            <i className="bi bi-calendar-check me-1"></i>
                            {formatDate(order.delivery.deliveryDate)}
                          </Badge>
                          {order.delivery.note && (
                            <><br /><small className="text-muted">{order.delivery.note}</small></>
                          )}
                        </>
                      ) : (
                        <Badge bg="secondary">ยังไม่กำหนด</Badge>
                      )}
                    </td>
                    <td>
                      {order.delivery?.setBy ? (
                        <small>
                          {order.delivery.setBy.fullName}
                          <br />
                          <span className="text-muted">{formatDateTime(order.delivery.updatedAt)}</span>
                        </small>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="text-center">
                      <Link to={`/orders/${order.id}`}>
                        <Button variant="info" size="sm" className="me-1" title="ดูรายละเอียด">
                          <i className="bi bi-eye"></i>
                        </Button>
                      </Link>
                      <Button
                        variant={order.delivery ? "warning" : "primary"}
                        size="sm"
                        className="me-1"
                        onClick={() => handleSetDeliveryDate(order)}
                        title={order.delivery ? "แก้ไขวันที่จัดส่ง" : "กำหนดวันที่จัดส่ง"}
                      >
                        <i className={order.delivery ? "bi bi-pencil" : "bi bi-calendar-plus"}></i>
                      </Button>
                      {order.delivery && (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDelete(order.id)}
                          title="ลบวันที่จัดส่ง"
                        >
                          <i className="bi bi-trash"></i>
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Set Delivery Date Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-calendar-plus me-2"></i>
            กำหนดวันที่จัดส่ง - คำสั่งซื้อ #{selectedOrder?.id}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            {selectedOrder && (
              <>
                <Row className="mb-3">
                  <Col>
                    <p className="mb-1"><strong>สาขา:</strong> {selectedOrder.orderedBy?.fullName}</p>
                    <p className="mb-1"><strong>มูลค่า:</strong> {selectedOrder.totalCost?.toLocaleString('th-TH', { minimumFractionDigits: 2 })} ฿</p>
                    <p className="mb-0"><strong>จำนวน:</strong> {selectedOrder.items?.length || 0} รายการ</p>
                  </Col>
                </Row>
                <hr />

                <Form.Group className="mb-3">
                  <Form.Label>วันที่จัดส่ง *</Form.Label>
                  <Form.Control
                    type="date"
                    value={deliveryDate}
                    onChange={(e) => setDeliveryDate(e.target.value)}
                    required
                  />
                  <Form.Text className="text-muted">
                    กำหนดวันที่ที่จะจัดส่งสินค้าให้กับสาขา
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>หมายเหตุ</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="หมายเหตุเพิ่มเติม (ถ้ามี)"
                  />
                </Form.Group>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)} disabled={processing}>
              ยกเลิก
            </Button>
            <Button variant="primary" type="submit" disabled={processing}>
              {processing ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  กำลังบันทึก...
                </>
              ) : (
                'บันทึก'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default ShippingManagement;
